import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import {
  isLinkValue,
  resolveLinkValueToUrl,
  type LinkValue,
  type TimerRedirectBlockConfig,
  timerRedirectBlockResolveConfig,
} from '@front-platform/content-builder';
import { Component, OnInit, input, computed } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { map, takeUntil, tap } from 'rxjs/operators';
import {
  LinkNavigationService,
  toShopRef,
} from '@core/services/link-navigation/link-navigation.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

export interface TimerRedirectBlockRuntimeConfig {}

export type TimerRedirectBlockConfigInput = Partial<
  Omit<TimerRedirectBlockConfig, keyof TimerRedirectBlockRuntimeConfig> &
    TimerRedirectBlockRuntimeConfig
>;
export type TimerRedirectBlockResolvedConfig = Omit<
  TimerRedirectBlockConfig,
  keyof TimerRedirectBlockRuntimeConfig
> &
  Partial<TimerRedirectBlockRuntimeConfig>;
export interface TimerRedirectBlockContract extends ConfigComponentContract<
  TimerRedirectBlockConfigInput,
  TimerRedirectBlockResolvedConfig
> {}

type TimerRedirectTarget = string | LinkValue;

@Component({
  selector: 'app-timer-redirect-block',
  template: ``,
  standalone: false,
})
export class TimerRedirectBlockComponent
  extends DestroyerBase
  implements OnInit, TimerRedirectBlockContract
{
  readonly config = input<TimerRedirectBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    TimerRedirectBlockConfig,
    TimerRedirectBlockRuntimeConfig
  >(timerRedirectBlockResolveConfig, () => ({ config: this.config() }));
  readonly redirectTo = computed(
    () => this.resolvedConfig()['redirect_to'] as unknown as TimerRedirectTarget | undefined,
  );
  readonly duration = computed(() => this.resolvedConfig()['duration'] as unknown as number);
  readonly resetUi = computed(() => this.resolvedConfig()['reset_ui'] as unknown as boolean);

  constructor(
    private readonly linkNavigation: LinkNavigationService,
    private readonly resetStateService: ResetStateService,
    private readonly analyticsService: AnalyticsService,
    private readonly shopSelector: ShopStoreSelector,
  ) {
    super();
  }

  ngOnInit(): void {
    const duration = this.duration();
    if (!duration || !this.hasRedirectTarget(this.redirectTo())) {
      return;
    }

    timer(Number(duration) * 1000)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(() => {
          this.resetUIIfNeeded().subscribe(({ willRedirectToHomePage }) => {
            const redirectTo = this.redirectTo();
            if (!this.hasRedirectTarget(redirectTo) || willRedirectToHomePage) {
              return;
            }
            this.linkNavigation.navigate(redirectTo);
          });
        }),
      )
      .subscribe();
  }

  private resetUIIfNeeded(): Observable<{ willRedirectToHomePage: boolean }> {
    if (this.resetUi()) {
      const willRedirectToAnotherPageThanHomePage = this.isNonHomeRedirect(this.redirectTo());
      return this.resetStateService
        .refreshUI({
          redirectToHomePage: !willRedirectToAnotherPageThanHomePage,
        })
        .pipe(map(() => ({ willRedirectToHomePage: !willRedirectToAnotherPageThanHomePage })));
    }

    return of({ willRedirectToHomePage: false });
  }

  private hasRedirectTarget(
    link: TimerRedirectTarget | undefined | null,
  ): link is TimerRedirectTarget {
    if (link == null) {
      return false;
    }
    if (typeof link === 'string') {
      return link.trim().length > 0;
    }
    if (!isLinkValue(link)) {
      return false;
    }
    if (link.kind === 'modal') {
      return link.handle.trim().length > 0;
    }
    return this.resolveLinkUrl(link).length > 0;
  }

  private isNonHomeRedirect(link: TimerRedirectTarget | undefined | null): boolean {
    if (!this.hasRedirectTarget(link)) {
      return false;
    }
    if (typeof link === 'string') {
      return link !== '/';
    }
    if (link.kind === 'modal') {
      return true;
    }
    return this.resolveLinkUrl(link) !== '/';
  }

  private resolveLinkUrl(link: Exclude<LinkValue, { kind: 'modal' }>): string {
    const resolvedUrl = resolveLinkValueToUrl(link, {
      resolveActiveShop: () => toShopRef(this.shopSelector.selectActive()),
    });
    return link.kind === 'page' && !resolvedUrl ? '/' : resolvedUrl;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(TimerRedirectBlockComponent, { 'keyAliases': {} });
